<script setup>
import { computed, ref } from "vue";
import ModalStatic from "@/components/ModalStatic.vue";
import MdcChips from "@/components/Mdc/MdcChips.vue";
import MdcButton from "@/components/Mdc/MdcButton.vue";
import { useI18n } from "vue-i18n";
import MdcChip from "@/components/Mdc/MdcChip.vue";

const props = defineProps({
    categories: {
        type: Array,
        required: true,
    },
    activeCategory: {
        required: true,
    },
    subCategories: {
        type: Array,
        required: true,
    },
    activeSubCategories: {
        type: Array,
        required: true,
    },
});

const categoriesOpen = ref(false);
const subCategoriesOpen = ref(false);

function setCategory(categoryId) {
    let c = props.categories.find((i) => i.value === categoryId);
    emit("setCategory", c);
    categoriesOpen.value = false;
}
const categoryLabel = computed(() => {
    if (props.activeCategory === 0) {
        return t("ContentHubCategoryLabel");
    } else {
        return props.categories.find((c) => {
            return c.value === props.activeCategory;
        }).label;
    }
});

function setSubCategories(subCategories) {
    emit("setSubCategories", subCategories);
}

const { t } = useI18n({
    messages: {
        en: {
            ContentHubCategoryLabel: "Choose Industry",
            ContentHubSubCategoryLabel: "Show all filters",
            ContentHubFilter: "Filter",
            ContentHubIndustries: "Industries",
            ContentHubApplyFilter: "Apply filter",
        },
        es: {
            ContentHubCategoryLabel: "Elegir sector",
            ContentHubSubCategoryLabel: "Mostrar todos los filtros",
            ContentHubFilter: "Filtro",
            ContentHubIndustries: "Industrias",
            ContentHubApplyFilter: "Aplicar filtro",
        },
        de: {
            ContentHubCategoryLabel: "Industrie wählen",
            ContentHubSubCategoryLabel: "Alle Filter anzeigen",
            ContentHubFilter: "Filter",
            ContentHubIndustries: "Branchen",
            ContentHubApplyFilter: "Filter anwenden",
        },
        pt: {
            ContentHubCategoryLabel: "Selecionar o sector",
            ContentHubSubCategoryLabel: "Mostrar todos os filtros",
            ContentHubFilter: "Filtro",
            ContentHubIndustries: "Indústrias",
            ContentHubApplyFilter: "Aplicar filtro",
        },
        ru: {
            ContentHubCategoryLabel: "Выберите отрасль",
            ContentHubSubCategoryLabel: "Показать все фильтры",
            ContentHubFilter: "Фильтр",
            ContentHubIndustries: "Промышленность",
            ContentHubApplyFilter: "Применить фильтр",
        },
        tr: {
            ContentHubCategoryLabel: "Sektör Seçiniz",
            ContentHubSubCategoryLabel: "Tüm filtreleri göster",
            ContentHubFilter: "Filtre",
            ContentHubIndustries: "Endüstriler",
            ContentHubApplyFilter: "Filtre uygula",
        },
        zh: {
            ContentHubCategoryLabel: "选择行业",
            ContentHubSubCategoryLabel: "显示所有筛选器",
            ContentHubFilter: "过滤器",
            ContentHubIndustries: "工业",
            ContentHubApplyFilter: "应用过滤器",
        },
    },
});

const emit = defineEmits(["setCategory", "setSubCategories"]);
</script>

<template>
    <div>
        <div class="categories">
            <MdcButton
                :label="categoryLabel"
                type="outlined"
                trailing-icon="expand_more"
                @click="categoriesOpen = true"
                style="width: 256px"
            />
        </div>
        <ModalStatic :active="categoriesOpen" @close="categoriesOpen = false">
            <p class="headline">{{ t("ContentHubIndustries") }}</p>
            <div class="categories__inner">
                <MdcChips
                    :list="props.categories"
                    @update="setCategory"
                    justify-content="flex-start"
                    align-items="flex-start"
                />
            </div>
        </ModalStatic>
        <div class="sub-categories" v-if="props.subCategories.length">
            <MdcChip
                :label="t('ContentHubSubCategoryLabel')"
                trailing-icon="tune"
                @click="subCategoriesOpen = true"
            />
        </div>
        <ModalStatic
            :active="subCategoriesOpen"
            @close="subCategoriesOpen = false"
        >
            <p class="headline">{{ t("ContentHubFilter") }}</p>
            <div class="sub-categories__inner">
                <div>
                    <MdcChips
                        :list="props.subCategories"
                        :active-chips="props.activeSubCategories"
                        @update="setSubCategories"
                        justify-content="flex-start"
                        align-items="flex-start"
                    />
                </div>
                <div style="padding-top: 32px; text-align: right">
                    <MdcButton
                        :label="t('ContentHubApplyFilter')"
                        @click="subCategoriesOpen = false"
                        type="raised"
                    />
                </div>
            </div>
        </ModalStatic>
    </div>
</template>

<style scoped lang="scss">
@use "@/scss/design-library/colors";
@use "@/scss/design-library/typography";
.categories {
    text-align: center;
    padding-bottom: 32px;
    border-bottom: 2px solid #{colors.$grey};

    &__inner {
        display: flex;
        flex-wrap: wrap;
        gap: 8px 12px;
    }
}
.sub-categories {
    padding-top: 32px;
}
.headline {
    @include typography.headline2;
    color: #{colors.$black};
    margin-bottom: 16px;
}

.mdc-evolution-chip-set__chips {
    justify-content: flex-start;
}
</style>
